import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";

import { Navbar, Nav, Dropdown, Container, Spinner } from "react-bootstrap";
import { Link, useNavigate, Outlet, useLocation } from "react-router-dom";

import { toast } from "react-toastify";
import { TUTOR_ONBOARDING_API, NOTIFICATION_API } from "../../api/api";

import { FaChevronDown, FaBars } from "react-icons/fa";
import { BsStars } from "react-icons/bs";

import Logo from "../../assets/Skilledition-logo.png";
// import NotificationImg from "../../assets/notification-icon.png";

import notificationIcon from "../../assets/icons/student/notification.svg";

import { initialsFromName } from "../../utils/getInitials";
import { handleApiError } from "../../utils/apiErrorHandler";

import "./DashboardHeader.css";

const notificationRouteMap = {
  tutor: {
    chat: "/header/tutor/student-doubts",
    "Final Assignment Submited": "/header/tutor/assignments",
    "Feedback Submitted": "/header/tutor/feedback",
  },

  admin: {
    "Final Assignment Submited": "/header/admin/assignments",
    "Course Purchased": "/header/admin/platform-analytics",
    "Tutor-Onboarding": "/header/admin/all-tutor",
    internship: "/header/admin/internship",
  },
};

const DashboardHeader = () => {
  const [userData, setUserData] = useState({
    full_name: "",
    role: "",
  });

  const [showNotifications, setShowNotifications] = useState(false);
  const [activeTab, setActiveTab] = useState("unread");
  const [notifications, setNotifications] = useState([]);

  const [activeLink, setActiveLink] = useState("");
  const [isNavOpen, setIsNavOpen] = useState(false);

  const dropdownRef = useRef(null);
  const navbarRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  //loading
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [loadingNotification, setLoadingNotification] = useState(true);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  // const handleAuthError = useCallback(
  //   (error) => {
  //     if (error.response?.status === 401) {
  //       showErrorToast(error.response.data?.message);

  //       localStorage.clear();

  //       setTimeout(() => {
  //         navigate("/signin");
  //       }, 2000);
  //       return true;
  //     }
  //     return false;
  //   },
  //   [navigate],
  // );

  useEffect(() => {
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      return;
    }

    const fetchUser = async () => {
      try {
        const response = await axios.post(
          TUTOR_ONBOARDING_API.TUTOR_USER_ROUTES_GET_USER,
          {
            user_id: userId,
          },
        );

        const data = response.data;
        if (data.statusCode === 200) {
          setUserData(data.user);
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        // Server responded with non-2xx status (like 400, 500)

        if (error.response?.data?.message) {
          showErrorToast(error.response.data.message);
        } else {
          showErrorToast("Network error! Please try again.");
        }
      } finally {
        setIsUserLoaded(true);
      }
    };

    fetchUser();
  }, []);

  // Define role once
  const role = userData.role;

  useEffect(() => {
    if (isUserLoaded && !role) {
      navigate("/signin");
    }
  }, [isUserLoaded, role, navigate]);

  const fetchNotification = useCallback(async () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setLoadingNotification(false);
      navigate("/signin");

      return;
    }

    try {
      const response = await axios.post(
        NOTIFICATION_API.GET_NOTIFICATION,
        { receiver_id: userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setNotifications(data.notification_data || []);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error fetch notification:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setLoadingNotification(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchNotification();
  }, [fetchNotification]);

  const handleMarkAsRead = async (notificationId) => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/signin");

      return;
    }

    try {
      const response = await axios.post(
        NOTIFICATION_API.MARK_NOTIFICATION_AS_READ,
        { notification_id: notificationId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        await fetchNotification();
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error updating Mark as read:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    }
  };

  const handleRemoveNotification = async (notificationId) => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/signin");

      return;
    }

    try {
      const response = await axios.post(
        NOTIFICATION_API.DELETE_NOTIFICATION,
        { notification_id: notificationId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        // toast.success(data.message, {
        //   position: "top-right",
        //   autoClose: 1500,
        //   theme: "colored",
        // });
        await fetchNotification();
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error Delete Notification:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    }
  };

  const handleNotificationClick = async (notification) => {
    const { notification_id, type, is_read } = notification;

    try {
      if (!is_read) {
        await handleMarkAsRead(notification_id);
      }

      const route = notificationRouteMap[role]?.[type];

      // Navigate based on type
      if (route) {
        navigate(route);
      }

      setShowNotifications(false);
    } catch (error) {
      console.error(error);
    }
  };

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications],
  );

  const readCount = useMemo(
    () => notifications.filter((n) => n.is_read).length,
    [notifications],
  );

  const filteredNotifications = useMemo(() => {
    if (activeTab === "unread") return notifications.filter((n) => !n.is_read);
    if (activeTab === "read") return notifications.filter((n) => n.is_read);
    return notifications;
  }, [notifications, activeTab]);

  // Menu items based on role
  const menuItems = useMemo(() => {
    if (!role) return []; // don't return anything until role is known

    if (role === "admin") {
      return [
        { name: "Dashboard", path: "/header/admin/dashboard" },

        {
          name: "Course Management",
          children: [
            { name: "All Courses", path: "/header/admin/all-courses" },
            {
              name: "Published Courses",
              path: "/header/admin/course-published",
            },
            { name: "Pending Courses", path: "/header/admin/course-pending" },
            { name: "Rejected Courses", path: "/header/admin/course-rejected" },
          ],
        },

        {
          name: "Tutor Management",
          children: [
            { name: "All Tutors", path: "/header/admin/all-tutor" },
            { name: "Published Tutors", path: "/header/admin/tutor-published" },
            { name: "Pending Tutors", path: "/header/admin/tutor-pending" },
            { name: "Rejected Tutors", path: "/header/admin/tutor-rejected" },
          ],
        },
        {
          name: "Students",
          children: [
            {
              name: "Student Details",
              path: "/header/admin/student-management",
            },
            {
              name: "Student Certificates",
              path: "/header/admin/student-certificates",
            },
          ],
        },
        { name: "Assignments", path: "/header/admin/assignments" },
        {
          name: "Reports",
          children: [
            {
              name: "Platform Analytics",
              path: "/header/admin/platform-analytics",
            },
            {
              name: "Student Payments",
              path: "/header/admin/student-payments",
            },
          ],
        },
        { name: "Internship", path: "/header/admin/internship" },
        {
          name: "Chats",
          children: [
            { name: "All Chats", path: "/header/admin/chats" },
            { name: "Paused Chats", path: "/header/admin/paused-chats" },
          ],
        },
      ];
    }

    if (role === "tutor") {
      return [
        { name: "Dashboard", path: "/header/tutor/dashboard" },
        { name: "My Courses", path: "/header/tutor/my-courses" },
        { name: "Assignments", path: "/header/tutor/assignments" },
        { name: "Student Doubts", path: "/header/tutor/student-doubts" },
        // { name: "Announcements", path: "/header/tutor/announcements" },
        { name: "Analytics", path: "/header/tutor/analytics" },
        { name: "Feedback", path: "/header/tutor/feedback" },
        { name: "Notifications", path: "/header/tutor/notifications" },
        { name: "Tutorial", path: "/header/tutor/tutorial", icon: <BsStars /> },
      ];
    }

    return [];
  }, [role]);

  // const markAllAsRead = () => {
  //   setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  // };

  useEffect(() => {
    const currentItem = menuItems.find((item) =>
      location.pathname.startsWith(item.path),
    );
    if (currentItem) {
      setActiveLink(currentItem.name);
    } else {
      setActiveLink(""); // clear active when outside header menu
    }
  }, [location.pathname, menuItems]);

  // Close notifications when clicked outside Close navbar when clicking outside (optional nice UX)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }

      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsNavOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("userRole");
    // localStorage.removeItem("tutor_id");
    // localStorage.removeItem("demo_video_id");

    // Navigate based on role
    if (role === "admin") {
      navigate("/admin-signin");
    } else {
      navigate("/signin");
    }
  };

  return (
    <>
      <div className="student-header-section">
        <Container className="student-header-container">
          {/* Top Header */}
          <div className="student-header-top">
            <div className="student-header-logo">
              <img
                src={Logo}
                alt="Skill Edition Logo"
                className="student-logo-img"
              />
            </div>

            {/* RIGHT: Notifications + Hamburger + Profile */}
            <div className="student-header-right" ref={dropdownRef}>
              {/* Notification Icon */}
              <div
                className="notification-icon"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <img
                  src={notificationIcon}
                  alt="Notifications"
                  className="notification-img"
                />
                {unreadCount > 0 && (
                  <span className="badge">{unreadCount}</span>
                )}
              </div>

              {/* Notification Dropdown */}
              {showNotifications && (
                <>
                  <div
                    className="notification-overlay"
                    onClick={() => setShowNotifications(false)}
                  />
                  <div className="notification-dropdown">
                    <h6>Notifications</h6>
                    <div className="tabs">
                      <button
                        className={activeTab === "all" ? "active" : ""}
                        onClick={() => setActiveTab("all")}
                      >
                        All ({notifications.length})
                      </button>
                      <button
                        className={activeTab === "unread" ? "active" : ""}
                        onClick={() => setActiveTab("unread")}
                      >
                        Unread ({unreadCount})
                      </button>
                      <button
                        className={activeTab === "read" ? "active" : ""}
                        onClick={() => setActiveTab("read")}
                      >
                        Read ({readCount})
                      </button>
                    </div>

                    <div className="notification-list">
                      {loadingNotification ? (
                        <div className="text-center mt-5">
                          <Spinner animation="border" variant="primary" />
                        </div>
                      ) : filteredNotifications.length > 0 ? (
                        filteredNotifications.map((n) => (
                          <div
                            key={n.notification_id}
                            className={`notification-item ${
                              n.is_read ? "read" : "unread"
                            }`}
                            onClick={() => handleNotificationClick(n)}
                          >
                            <div className="notification-header">
                              <strong>{n.type || ""}</strong>
                              <button
                                className="close-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveNotification(n.notification_id);
                                }}
                              >
                                ×
                              </button>
                            </div>
                            <p>{n.message || ""}</p>

                            {!n.is_read && (
                              <button
                                className="okay-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // mark as read + navigate together
                                  handleNotificationClick(n);
                                }}
                              >
                                Okay
                              </button>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="no-notifications">
                          No notifications yet 🎉
                        </p>
                      )}
                    </div>

                    {/* <div className="mark-read" onClick={markAllAsRead}>
                      Mark all as read
                    </div> */}
                  </div>
                </>
              )}

              {/* Profile Dropdown (Desktop) */}
              <Dropdown className="profile-dropdown" align="end">
                <Dropdown.Toggle className="profile-btn" id="dropdown-basic">
                  <div className="profile-info">
                    <div className="profile-icon">
                      {initialsFromName(userData.full_name)}
                    </div>
                    <div className="profile-text">
                      <span className="profile-name" title={userData.full_name}>
                        {userData.full_name || "User"}
                      </span>
                      <span className="profile-role">
                        {role === "admin" ? "Admin" : "Tutor"}
                      </span>
                    </div>
                    <FaChevronDown className="dropdown-arrow" />
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu className="profile-menu">
                  {role === "tutor" && (
                    <>
                      <Dropdown.Item as={Link} to={"/header/tutor/profile"}>
                        Profile
                      </Dropdown.Item>

                      <Dropdown.Item as={Link} to="/header/tutor/settings">
                        Settings
                      </Dropdown.Item>

                      {/* <Dropdown.Item as={Link} to="/header/tutor/payments">
                        Payments
                      </Dropdown.Item> */}
                    </>
                  )}
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          <Navbar
            expand="lg"
            expanded={isNavOpen}
            onToggle={(expanded) => setIsNavOpen(expanded)}
            ref={navbarRef}
            className="student-header-navbar p-0 d-flex justify-content-end align-items-center"
          >
            <Navbar.Toggle aria-controls="basic-navbar-nav">
              <FaBars color="#000" size={22} />
            </Navbar.Toggle>
            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="student-header-nav flex-column flex-lg-row">
                {menuItems.map((item) =>
                  item.children ? (
                    <Dropdown
                      key={item.name}
                      className="student-header-nav-dropdown"
                      onClick={() => setActiveLink(item.name)}
                    >
                      <Dropdown.Toggle
                        className={`student-header-link student-header-dropdown-toggle-custom ${
                          activeLink === item.name ? "active" : ""
                        }`}
                      >
                        {item.name}
                        <FaChevronDown className="ms-1" size={12} />
                      </Dropdown.Toggle>

                      <Dropdown.Menu className="student-header-admin-dropdown-menu">
                        {item.children.map((sub) => (
                          <Dropdown.Item
                            key={sub.name}
                            as={Link}
                            to={sub.path}
                            className="student-header-admin-dropdown-item"
                            onClick={() => setIsNavOpen(false)}
                          >
                            {sub.name}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.path}
                      className={`student-header-link ${
                        activeLink === item.name ? "active" : ""
                      } ${item.name === "Tutorial" ? "always-blue" : ""}`}
                      onClick={() => {
                        setActiveLink(item.name);
                        setIsNavOpen(false);
                      }}
                    >
                      {item.icon && (
                        <span className="student-header-icon">{item.icon}</span>
                      )}
                      {item.name}
                    </Link>
                  ),
                )}
              </Nav>
            </Navbar.Collapse>
          </Navbar>
        </Container>
      </div>
      {/* PAGE CONTENT */}
      <Container>
        <Outlet />
      </Container>
    </>
  );
};

export default DashboardHeader;
